import type { GameId } from '@tg/shared';
import { ART_PAL, svgDoc, type ArtPal, type ArtTheme } from './kit';
import { crashCover, crashHeroCover } from './crash';
import { potCover } from './pot';
import { rouletteCover } from './roulette';
import { gardenCover } from './garden';
import { deepCover } from './deep';
import { spaceportCover } from './spaceport';
import { sweetShopCover } from './sweetShop';
import { workshopCover } from './workshop';
import { carpCover } from './carp';
import { koiPondCover } from './koiPond';
import { luckyLeverCover } from './luckyLever';
import { neonArcadeCover } from './neonArcade';
import { pirateCoveCover } from './pirateCove';
import { firecrackersCover } from './firecrackers';
import { hongbaoCover } from './hongbao';
import { mahjongCover } from './mahjong';
import { mahjongTableCover } from './mahjong-table';
import { zhajinhuaCover } from './zhajinhua';
import { doudizhuCover } from './doudizhu';
import { chessCover, colorSortCover, dailyWordsCover, tileMatchCover } from './skill';

/** Every lobby cover (16:10, 640×400) plus the wide Liftoff hero (1200×400). */
export type CoverId = GameId | 'crash-hero';

type Builder = (P: ArtPal, prefix: string) => string;

const BUILDERS: Record<CoverId, { w: number; h: number; build: Builder }> = {
  crash: { w: 640, h: 400, build: crashCover },
  'crash-hero': { w: 1200, h: 400, build: crashHeroCover },
  pot: { w: 640, h: 400, build: potCover },
  roulette: { w: 640, h: 400, build: rouletteCover },
  garden: { w: 640, h: 400, build: gardenCover },
  deep: { w: 640, h: 400, build: deepCover },
  spaceport: { w: 640, h: 400, build: spaceportCover },
  'sweet-shop': { w: 640, h: 400, build: sweetShopCover },
  workshop: { w: 640, h: 400, build: workshopCover },
  'koi-pond': { w: 640, h: 400, build: koiPondCover },
  'lucky-lever': { w: 640, h: 400, build: luckyLeverCover },
  'neon-arcade': { w: 640, h: 400, build: neonArcadeCover },
  'pirate-cove': { w: 640, h: 400, build: pirateCoveCover },
  carp: { w: 640, h: 400, build: carpCover },
  firecrackers: { w: 640, h: 400, build: firecrackersCover },
  hongbao: { w: 640, h: 400, build: hongbaoCover },
  mahjong: { w: 640, h: 400, build: mahjongCover },
  'mahjong-table': { w: 640, h: 400, build: mahjongTableCover },
  zhajinhua: { w: 640, h: 400, build: zhajinhuaCover },
  doudizhu: { w: 640, h: 400, build: doudizhuCover },
  'tile-match': { w: 640, h: 400, build: tileMatchCover },
  'color-sort': { w: 640, h: 400, build: colorSortCover },
  'daily-words': { w: 640, h: 400, build: dailyWordsCover },
  chess: { w: 640, h: 400, build: chessCover },
};

export const COVER_IDS = Object.keys(BUILDERS) as CoverId[];

export function coverSize(id: CoverId): { w: number; h: number } {
  const b = BUILDERS[id];
  return { w: b.w, h: b.h };
}

/** Prefix for <defs> ids: unique per cover + theme (+ optional instance suffix). */
const idPrefix = (id: CoverId, theme: ArtTheme, suffix = '') =>
  `c${id.replace(/[^a-z]/g, '')}${theme[0]}${suffix}`;

/** Inner markup of a cover in its own coordinates (for inlining into another SVG). */
export function coverMarkup(id: CoverId, theme: ArtTheme, suffix = ''): string {
  return BUILDERS[id].build(ART_PAL[theme], idPrefix(id, theme, suffix));
}

/** Standalone SVG document of a cover at its native size. */
export function coverSvg(id: CoverId, theme: ArtTheme, suffix = ''): string {
  const { w, h } = BUILDERS[id];
  return svgDoc(w, h, coverMarkup(id, theme, suffix));
}

/**
 * Cover cropped to any tile size: the art keeps its aspect and is anchored to the right edge
 * (focus object lives in the right half), so square / narrow tiles still show it.
 */
export function coverSvgSized(id: CoverId, w: number, h: number, theme: ArtTheme): string {
  const b = BUILDERS[id];
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}">` +
    `<svg width="${w}" height="${h}" viewBox="0 0 ${b.w} ${b.h}" preserveAspectRatio="xMaxYMid slice">${coverMarkup(id, theme)}</svg></svg>`
  );
}

export { ART_PAL, type ArtPal, type ArtTheme } from './kit';
